"use client";
import React, { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import Image from "next/image";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import { firestore } from "../app/firebase";
import { toast } from "sonner";
import CheckoutPopup from "./checkout/checkout"; // Import your checkout popup

const SideCart = ({ isOpen, onClose, selectedProduct }) => {
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [cartProduct, setCartProduct] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCheckoutPopup, setShowCheckoutPopup] = useState(false);
  const shippingCost = 99;
  const [subtotal, setSubtotal] = useState(0);
  const [discount, setDiscount] = useState(0);

  // Calculate final total
  const finalTotal = subtotal - discount + shippingCost;

  // Load cart from localStorage on initial render
  useEffect(() => {
    try {
      const storedCart = JSON.parse(localStorage.getItem("cart") || "[]");
      setCartProduct(storedCart);

      // Calculate subtotal
      const calculatedSubtotal = storedCart.reduce(
        (sum, item) =>
          sum + (item.price || item.productPrice) * (item.quantity || 1),
        0
      );
      setSubtotal(calculatedSubtotal);
    } catch (error) {
      console.error("Error loading cart from localStorage:", error);
      setCartProduct([]);
      setSubtotal(0);
    }
  }, []);

  // Handle the selected product from parent component
  useEffect(() => {
    if (!selectedProduct) return;

    try {
      // Load the latest cart from localStorage to ensure we have current data
      const storedCart = JSON.parse(localStorage.getItem("cart") || "[]");

      // Only if the cart in state is different from localStorage, update it
      if (JSON.stringify(cartProduct) !== JSON.stringify(storedCart)) {
        setCartProduct(storedCart);

        // Update subtotal after cart changes
        const newSubtotal = storedCart.reduce(
          (sum, item) =>
            sum + (item.price || item.productPrice) * (item.quantity || 1),
          0
        );
        setSubtotal(newSubtotal);
      }
    } catch (error) {
      console.error("Error syncing cart with localStorage:", error);
    }
  }, [selectedProduct, cartProduct]);

  // Add recommended product to cart
  const addRecommendedToCart = (product) => {
    try {
      // First get the current cart from localStorage to ensure we're working with latest data
      const currentCart = JSON.parse(localStorage.getItem("cart") || "[]");

      // Look for existing product
      const existingProductIndex = currentCart.findIndex(
        (item) => item.id === product.id
      );

      if (existingProductIndex > -1) {
        // Update quantity if product exists
        currentCart[existingProductIndex].quantity += 1;
      } else {
        // Add new product with normalized structure
        currentCart.push({
          id: product.id,
          name: product.productName,
          productName: product.productName, // Keep both for compatibility
          price: product.productPrice,
          productPrice: product.productPrice, // Keep both for compatibility
          customImage: product.productImages || ["/api/placeholder/100/100"],
          image: product.productImages?.[0], // Keep for compatibility
          quantity: 1,
          Category: product.Category,
          category: product.Category, // Keep both for compatibility
        });
      }

      // Update localStorage
      localStorage.setItem("cart", JSON.stringify(currentCart));

      // Update state
      setCartProduct(currentCart);

      // Update subtotal
      const newSubtotal = currentCart.reduce(
        (sum, item) =>
          sum + (item.price || item.productPrice) * (item.quantity || 1),
        0
      );
      setSubtotal(newSubtotal);

      toast.success("Added to cart!");
    } catch (error) {
      console.error("Error adding recommended product to cart:", error);
      toast.error("Failed to add product to cart");
    }
  };

  // Update cart item quantity
  const updateCartItemQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeCartItem(productId);
      return;
    }

    try {
      const updatedCart = cartProduct.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      );

      // Update localStorage
      localStorage.setItem("cart", JSON.stringify(updatedCart));

      // Update state
      setCartProduct(updatedCart);

      // Update subtotal
      const newSubtotal = updatedCart.reduce(
        (sum, item) =>
          sum + (item.price || item.productPrice) * (item.quantity || 1),
        0
      );
      setSubtotal(newSubtotal);
    } catch (error) {
      console.error("Error updating cart item quantity:", error);
      toast.error("Failed to update quantity");
    }
  };

  // Fetch recommended products based on cart contents
  useEffect(() => {
    const fetchRecommendations = async () => {
      if (cartProduct.length === 0) return;

      try {
        setIsLoading(true);

        // Get unique categories from cart items that are not undefined or null
        const cartCategories = [
          ...new Set(
            cartProduct
              .map((item) => item.Category || item.category)
              .filter((category) => category) // Filter out undefined/null
          ),
        ];

        // If no valid categories are found, fetch from "Best Selling" only
        if (cartCategories.length === 0) {
          const bestSellingQuery = query(
            collection(firestore, "products"),
            where("productTag", "==", "Best Selling"),
            limit(4)
          );

          const bestSellingSnapshot = await getDocs(bestSellingQuery);
          const bestSellingProducts = bestSellingSnapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter(
              (product) => !cartProduct.some((item) => item.id === product.id)
            );

          setRecommendedProducts(bestSellingProducts);
          setIsLoading(false);
          return;
        }

        // Get a random category from the cart
        const randomCategory =
          cartCategories[Math.floor(Math.random() * cartCategories.length)];

        // First, fetch best selling products
        const bestSellingQuery = query(
          collection(firestore, "products"),
          where("productTag", "==", "Best Selling"),
          limit(2)
        );

        const bestSellingSnapshot = await getDocs(bestSellingQuery);
        let bestSellingProducts = bestSellingSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter(
            (product) => !cartProduct.some((item) => item.id === product.id)
          );

        // Then fetch category-based recommendations for remaining slots
        const remainingLimit = 6 - bestSellingProducts.length;

        // Make sure category is valid before querying
        if (randomCategory) {
          const categoryQuery = query(
            collection(firestore, "products"),
            where("Category", "==", randomCategory),
            limit(remainingLimit)
          );

          const categorySnapshot = await getDocs(categoryQuery);

          // Process category-based results
          const categoryRecommendations = categorySnapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter(
              (product) => !cartProduct.some((item) => item.id === product.id)
            )
            .filter(
              (product) =>
                !bestSellingProducts.some((bp) => bp.id === product.id)
            ); // Exclude products already in best selling

          // Combine best selling products with category recommendations
          const recommendations = [
            ...bestSellingProducts,
            ...categoryRecommendations,
          ];
          setRecommendedProducts(recommendations);
        } else {
          // If no valid category found, just use best selling products
          setRecommendedProducts(bestSellingProducts);
        }
      } catch (error) {
        console.error("Error fetching recommendations:", error);
        // Silently fail for recommendations to not disrupt user experience
        setRecommendedProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendations();
  }, [cartProduct]); // Update recommendations when cart changes

  // Remove an item from the cart
  const removeCartItem = (productId) => {
    try {
      // Filter out the product to remove
      const updatedCart = cartProduct.filter((item) => item.id !== productId);

      // Update localStorage
      localStorage.setItem("cart", JSON.stringify(updatedCart));

      // Update state
      setCartProduct(updatedCart);

      // Update subtotal
      const newSubtotal = updatedCart.reduce(
        (sum, item) =>
          sum + (item.price || item.productPrice) * (item.quantity || 1),
        0
      );
      setSubtotal(newSubtotal);

      toast.info("Product removed from cart");
    } catch (error) {
      console.error("Error removing item from cart:", error);
      toast.error("Failed to remove product from cart");
    }
  };

  // Handle checkout popup
  const handleCheckoutClick = () => {
    if (cartProduct.length === 0) return;
    setShowCheckoutPopup(true);
  };

  const handleCheckoutClose = () => {
    setShowCheckoutPopup(false);
    // Refresh cart data after checkout
    try {
      const storedCart = JSON.parse(localStorage.getItem("cart") || "[]");
      setCartProduct(storedCart);
      const calculatedSubtotal = storedCart.reduce(
        (sum, item) =>
          sum + (item.price || item.productPrice) * (item.quantity || 1),
        0
      );
      setSubtotal(calculatedSubtotal);
    } catch (error) {
      console.error("Error refreshing cart after checkout:", error);
    }
  };

  if (!isOpen) return null;

  // Calculate total including shipping
  const total = subtotal + shippingCost;

  return (
    <>
      <div
        className={`fixed right-0 top-0 h-full w-full md:w-96 bg-white shadow-xl transform transition-transform duration-500 ease-out z-50 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-black text-white p-4 flex justify-between items-center">
            <h2 className="text-lg font-medium">
              Your Cart ({cartProduct.length})
            </h2>
            <button onClick={onClose} className="p-2">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-4">
              {cartProduct.length > 0 ? (
                cartProduct.map((item) => (
                  <div key={item.id} className="flex gap-3 relative">
                    <div className="relative w-16 h-16 rounded-lg overflow-hidden">
                      <Image
                        src={
                          Array.isArray(item.customImage)
                            ? item.customImage[0]
                            : item.image ||
                              item.productImages?.[0] ||
                              "/api/placeholder/64/64"
                        }
                        alt={item.name || item.productName}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <button
                      onClick={() => removeCartItem(item.id)}
                      className="absolute top-0 right-0 text-red-500 hover:bg-red-100 rounded-full p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium">
                        {item.name || item.productName}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {item.Category || item.category}
                      </p>
                      {item.variant && (
                        <p className="text-xs text-gray-600">{item.variant}</p>
                      )}
                      <div className="mt-1 flex justify-between items-center">
                        <span className="text-sm font-bold">
                          ₹{item.price || item.productPrice}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              updateCartItemQuantity(
                                item.id,
                                (item.quantity || 1) - 1
                              )
                            }
                            className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs"
                          >
                            -
                          </button>
                          <span className="text-xs text-gray-500 min-w-[20px] text-center">
                            {item.quantity || 1}
                          </span>
                          <button
                            onClick={() =>
                              updateCartItemQuantity(
                                item.id,
                                (item.quantity || 1) + 1
                              )
                            }
                            className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Your cart is empty</p>
                </div>
              )}
            </div>

            {/* Recommendations */}
            {cartProduct.length > 0 && (
              <div className="p-4 border-t">
                <h3 className="text-sm font-medium mb-4">
                  Recommended For You
                </h3>
                {isLoading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse flex gap-3">
                        <div className="bg-gray-200 w-16 h-16 rounded-lg" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-gray-200 rounded w-3/4" />
                          <div className="h-3 bg-gray-200 rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : recommendedProducts.length > 0 ? (
                  <div className="space-y-4">
                    {recommendedProducts.map((product, index) => (
                      <div
                        key={product.id}
                        className="flex gap-3 group relative"
                      >
                        <div className="relative w-16 h-16 rounded-lg overflow-hidden">
                          <Image
                            src={
                              product.productImages?.[0] ||
                              "/api/placeholder/64/64"
                            }
                            alt={product.productName}
                            fill
                            className="object-cover"
                          />
                          {index < 2 &&
                            product.productTag?.includes("Best Selling") && (
                              <div className="absolute top-0 left-0 bg-yellow-500 text-white text-xs px-1 py-0.5">
                                Best Seller
                              </div>
                            )}
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium">
                            {product.productName}
                          </h4>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-sm font-bold">
                              ₹{product.productPrice}
                            </span>
                            <button
                              onClick={() => addRecommendedToCart(product)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Plus className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500 text-sm">
                      No recommendations available
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Checkout */}
          <div className="p-4 bg-gray-50 border-t">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Shipping</span>
                <span>₹{shippingCost}</span>
              </div>
              <div className="flex justify-between font-medium pt-2 border-t">
                <span>Total</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
            </div>

            {/* Checkout Button - Popup Only */}
            {cartProduct.length > 0 ? (
              <button
                onClick={handleCheckoutClick}
                className="w-full bg-red-500 text-white py-3 rounded font-medium hover:bg-red-600 transition-colors"
              >
                Proceed to Checkout
              </button>
            ) : (
              <button className="w-full bg-gray-200 py-3 rounded font-medium text-gray-500 cursor-not-allowed">
                Proceed to Checkout
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Checkout Popup */}
      <CheckoutPopup
        isOpen={showCheckoutPopup}
        onClose={handleCheckoutClose}
        cartItems={cartProduct}
        source="cart"
      />
    </>
  );
};

export default SideCart;
